import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { t, getCurrentLanguage } from '../utils/translations';
import Navbar from '../components/Navabar';
import Footer from '../components/Footer';
import api from '../components/api';
import { 
  Users, 
  FileText, 
  CheckCircle,
  AlertCircle,
  Building,
  User,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  Calendar,
  CreditCard,
  Send
} from 'lucide-react';

const AdhesionPage = () => {
  const navigate = useNavigate();
  const [currentLanguage, setCurrentLanguage] = useState(getCurrentLanguage());
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    full_name: '',
    birth_date: '',
    address: '',
    profession: '',
    phone: '',
    email: '',
    applicant_type: 'individual',
    member_type: 'active',
    payment_receipt: null,
    payment_amount: ''
  });

  useEffect(() => {
    const handleLanguageChange = () => {
      setCurrentLanguage(getCurrentLanguage());
    };
    window.addEventListener('languageChanged', handleLanguageChange);
    return () => window.removeEventListener('languageChanged', handleLanguageChange);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (files && files[0]) {
      setFormData(prev => ({
        ...prev,
        [name]: files[0]
      }));
    }
  };

  const getRequiredAmount = () => {
    return formData.applicant_type === 'individual' ? 25000 : 100000;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const submitData = new FormData();
      
      // Ajouter tous les champs texte
      Object.keys(formData).forEach(key => {
        if (formData[key] && typeof formData[key] !== 'object') {
          submitData.append(key, formData[key]);
        }
      });

      // Ajouter uniquement le reçu de paiement (obligatoire)
      if (formData.payment_receipt) {
        submitData.append('payment_receipt', formData.payment_receipt);
      }

      const response = await api.post('/api/memberships/', submitData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setSuccess(true);
      setFormData({
        full_name: '',
        birth_date: '',
        address: '',
        profession: '',
        phone: '',
        email: '',
        applicant_type: 'individual',
        member_type: 'active',
        payment_receipt: null,
        payment_amount: ''
      });

      // Scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });

    } catch (err) {
      console.error('Erreur lors de la soumission:', err);
      setError(currentLanguage === 'fr' 
        ? 'Une erreur est survenue lors de la soumission. Veuillez réessayer.' 
        : 'An error occurred during submission. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const memberTypes = [
    { value: 'founder', label: currentLanguage === 'fr' ? 'Membre fondateur' : 'Founding Member' },
    { value: 'active', label: currentLanguage === 'fr' ? 'Membre actif' : 'Active Member' },
    { value: 'honorary', label: currentLanguage === 'fr' ? 'Membre d\'honneur' : 'Honorary Member' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative h-[280px] mb-12 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#089556] via-[#07D1EB] to-[#0ba8c7]">
          <div className="absolute inset-0 opacity-[0.15]">
            <div className="absolute inset-0" style={{
              backgroundImage: `
                radial-gradient(circle at 20% 30%, rgba(255,255,255,0.8) 2px, transparent 2px),
                radial-gradient(circle at 80% 70%, rgba(255,255,255,0.6) 2px, transparent 2px)
              `,
              backgroundSize: '120px 120px, 100px 100px'
            }}></div>
          </div>

          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center relative z-10 max-w-4xl mx-auto px-4">
              <div className="inline-flex items-center gap-2 mb-6">
                <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/30">
                  <Users className="h-5 w-5 text-white" />
                </div>
                <span className="text-sm font-medium text-white/90 uppercase tracking-widest">
                  {currentLanguage === 'fr' ? 'Rejoignez-nous' : 'Join Us'}
                </span>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight drop-shadow-lg mb-6 leading-tight">
                {currentLanguage === 'fr' ? 'Formulaire d\'Adhésion' : 'Membership Application Form'}
              </h1>
              <p className="text-lg sm:text-xl text-white/90 max-w-3xl mx-auto leading-relaxed drop-shadow-sm">
                {currentLanguage === 'fr' 
                  ? 'Devenez membre de l\'ONG Action-Humanitaire Bénin et contribuez à nos actions humanitaires'
                  : 'Become a member of Action-Humanitaire Bénin NGO and contribute to our humanitarian actions'}
              </p>
              <div className="w-32 h-1 bg-white/60 mx-auto mt-8 rounded-full"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Success Message */}
      {success && (
        <div className="max-w-4xl mx-auto px-4 mb-8">
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 flex items-start gap-4">
            <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
            <div>
              <h3 className="text-lg font-semibold text-green-900 mb-2">
                {currentLanguage === 'fr' ? 'Demande soumise avec succès !' : 'Application submitted successfully!'}
              </h3>
              <p className="text-green-700">
                {currentLanguage === 'fr' 
                  ? 'Votre demande d\'adhésion a été reçue. Vous recevrez une confirmation par email dans les prochains jours.'
                  : 'Your membership application has been received. You will receive a confirmation email in the coming days.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="max-w-4xl mx-auto px-4 mb-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 flex items-start gap-4">
            <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
            <div>
              <h3 className="text-lg font-semibold text-red-900 mb-2">
                {currentLanguage === 'fr' ? 'Erreur' : 'Error'}
              </h3>
              <p className="text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Form Section */}
      <section className="max-w-4xl mx-auto px-4 pb-12">
        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-lg p-8">
          
          {/* Type de demandeur */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Building className="w-6 h-6 text-[#089556]" />
              {currentLanguage === 'fr' ? 'Type de demandeur' : 'Applicant Type'}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className={`flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                formData.applicant_type === 'individual' ? 'border-[#089556] bg-[#089556]/5' : 'border-gray-200 hover:border-[#089556]/50'
              }`}>
                <input
                  type="radio"
                  name="applicant_type"
                  value="individual"
                  checked={formData.applicant_type === 'individual'}
                  onChange={handleInputChange}
                  className="w-5 h-5 text-[#089556]"
                />
                <div>
                  <User className="w-5 h-5 text-[#089556] mb-1" />
                  <span className="font-medium text-gray-800">
                    {currentLanguage === 'fr' ? 'Particulier' : 'Individual'}
                  </span>
                  <p className="text-sm text-gray-600">25,000 FCFA</p>
                </div>
              </label>

              <label className={`flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                formData.applicant_type === 'company' ? 'border-[#089556] bg-[#089556]/5' : 'border-gray-200 hover:border-[#089556]/50'
              }`}>
                <input
                  type="radio"
                  name="applicant_type"
                  value="company"
                  checked={formData.applicant_type === 'company'}
                  onChange={handleInputChange}
                  className="w-5 h-5 text-[#089556]"
                />
                <div>
                  <Building className="w-5 h-5 text-[#089556] mb-1" />
                  <span className="font-medium text-gray-800">
                    {currentLanguage === 'fr' ? 'Entreprise' : 'Company'}
                  </span>
                  <p className="text-sm text-gray-600">100,000 FCFA</p>
                </div>
              </label>
            </div>
          </div>

          {/* Informations personnelles */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <FileText className="w-6 h-6 text-[#089556]" />
              {currentLanguage === 'fr' ? 'Informations Personnelles' : 'Personal Information'}
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {currentLanguage === 'fr' ? 'Nom et Prénom' : 'Full Name'} *
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleInputChange}
                    required
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#089556] focus:border-transparent"
                    placeholder={currentLanguage === 'fr' ? 'Votre nom complet' : 'Your full name'}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {currentLanguage === 'fr' ? 'Date de naissance' : 'Date of Birth'}
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="date"
                    name="birth_date"
                    value={formData.birth_date}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#089556] focus:border-transparent"
                  />
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {currentLanguage === 'fr' ? 'Adresse complète' : 'Full Address'} *
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-4 w-5 h-5 text-gray-400" />
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    required
                    rows="3"
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#089556] focus:border-transparent"
                    placeholder={currentLanguage === 'fr' ? 'Votre adresse complète' : 'Your full address'}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {currentLanguage === 'fr' ? 'Profession' : 'Profession'} *
                </label>
                <div className="relative">
                  <Briefcase className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    name="profession"
                    value={formData.profession}
                    onChange={handleInputChange}
                    required
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#089556] focus:border-transparent"
                    placeholder={currentLanguage === 'fr' ? 'Votre profession' : 'Your profession'}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {currentLanguage === 'fr' ? 'Téléphone' : 'Phone'} *
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#089556] focus:border-transparent"
                    placeholder="+229 XX XX XX XX"
                  />
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#089556] focus:border-transparent"
                    placeholder="votre@email.com"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Catégorie de membre */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              {currentLanguage === 'fr' ? 'Catégorie de membre souhaitée' : 'Desired Membership Category'}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {memberTypes.map(type => (
                <label key={type.value} className={`flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  formData.member_type === type.value ? 'border-[#089556] bg-[#089556]/5' : 'border-gray-200 hover:border-[#089556]/50'
                }`}>
                  <input
                    type="radio"
                    name="member_type"
                    value={type.value}
                    checked={formData.member_type === type.value}
                    onChange={handleInputChange}
                    className="w-5 h-5 text-[#089556]"
                  />
                  <span className="font-medium text-gray-800">{type.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Informations de paiement */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <CreditCard className="w-6 h-6 text-[#089556]" />
              {currentLanguage === 'fr' ? 'Frais d\'adhésion' : 'Membership Fee'}
            </h2>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
              <h3 className="font-semibold text-blue-900 mb-3">
                {currentLanguage === 'fr' ? 'Montant à payer :' : 'Amount to pay:'}
              </h3>
              <p className="text-3xl font-bold text-blue-900 mb-4">{getRequiredAmount().toLocaleString()} FCFA</p>
              
              <div className="space-y-2 text-sm text-blue-800">
                <p><strong>{currentLanguage === 'fr' ? 'Banque :' : 'Bank:'}</strong> LA POSTE</p>
                <p><strong>{currentLanguage === 'fr' ? 'Numéro de compte :' : 'Account Number:'}</strong> 191044804010</p>
                <p><strong>CODE SWIFT:</strong> BCAOBJBJ</p>
                <p><strong>{currentLanguage === 'fr' ? 'Bénéficiaire :' : 'Beneficiary:'}</strong> Action-Humanitaire Bénin</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {currentLanguage === 'fr' ? 'Reçu de paiement' : 'Payment Receipt'} *
                </label>
                <input
                  type="file"
                  name="payment_receipt"
                  onChange={handleFileChange}
                  required
                  accept=".pdf,.jpg,.jpeg,.png"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#089556] focus:border-transparent"
                />
                <p className="text-sm text-gray-500 mt-2">
                  {currentLanguage === 'fr' 
                    ? 'Formats acceptés : PDF, JPG, JPEG, PNG' 
                    : 'Accepted formats: PDF, JPG, JPEG, PNG'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {currentLanguage === 'fr' ? 'Montant payé (FCFA)' : 'Amount Paid (FCFA)'} *
                </label>
                <input
                  type="number"
                  name="payment_amount"
                  value={formData.payment_amount}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#089556] focus:border-transparent"
                  placeholder={getRequiredAmount().toString()}
                />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-center">
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-[#089556] to-[#07D1EB] text-white px-8 py-4 rounded-lg font-semibold hover:shadow-lg hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  {currentLanguage === 'fr' ? 'Envoi en cours...' : 'Sending...'}
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  {currentLanguage === 'fr' ? 'Soumettre ma demande' : 'Submit my application'}
                </>
              )}
            </button>
          </div>
        </form>
      </section>

      <Footer />
    </div>
  );
};

export default AdhesionPage;
